import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../lib/ThemeContext';

export default function ParticleBackground() {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const animRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    let t = 0;
    let visible = true;
    const handleVis = () => { visible = !document.hidden; };
    document.addEventListener('visibilitychange', handleVis);

    const animate = () => {
      if (!visible) { animRef.current = requestAnimationFrame(animate); return; }
      t += 0.002;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Soft gradient orbs
      const orbs = [
        { x: 0.15 + Math.sin(t * 0.7) * 0.05, y: 0.2 + Math.cos(t * 0.5) * 0.05, r: 0.35, color: isDark ? 'rgba(0,113,227,0.06)' : 'rgba(0,113,227,0.04)' },
        { x: 0.8 + Math.cos(t * 0.6) * 0.04, y: 0.3 + Math.sin(t * 0.8) * 0.04, r: 0.3, color: isDark ? 'rgba(6,182,212,0.05)' : 'rgba(6,182,212,0.03)' },
        { x: 0.5 + Math.sin(t * 0.4) * 0.06, y: 0.8 + Math.cos(t * 0.3) * 0.03, r: 0.4, color: isDark ? 'rgba(0,113,227,0.04)' : 'rgba(0,113,227,0.025)' },
      ];

      for (const orb of orbs) {
        const cx = orb.x * canvas.width;
        const cy = orb.y * canvas.height;
        const radius = orb.r * Math.max(canvas.width, canvas.height);
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
        gradient.addColorStop(0, orb.color);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, [isDark]);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
}
